import { useAtomValue, useSetAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { useCallback } from 'react';
import { PlainLanguage } from '../editor/code/Prism';
import { DEFAULT_EMOJI_NAMES, getEmojiItemByChar } from '../utils/emoji';

const storageKeys = {
  recentlyUsedEmojis: 'slabRecentlyUsedEmojis',
  recentlyMentionedUserIds: 'slabRecentlyMentionedUserIds',
  recentlyUsedTemplateIds: 'slabRecentlyUsedTemplateIds',
  recentlyViewedPostIds: 'slabRecentlyViewedPostIds',
  popularCodeLanguages: 'slabPopularCodeLanguages',
  lastCodeBlockSyntax: 'slabLastCodeBlockSyntax',
  emojiSkinToneModifier: 'slabEmojiSkinToneModifier',
} as const;

const recentlyUsedEmojisAtom = atomWithStorage(
  storageKeys.recentlyUsedEmojis,
  DEFAULT_EMOJI_NAMES,
);

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const recentlyMentionedUserIdsAtom = atomWithStorage<string[]>(
  storageKeys.recentlyMentionedUserIds,
  [],
);

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const recentlyUsedTemplateIdsAtom = atomWithStorage<string[]>(
  storageKeys.recentlyUsedTemplateIds,
  [],
);

const recentlyViewedPostIdsAtom = atomWithStorage<string[]>(
  storageKeys.recentlyViewedPostIds,
  [],
);

const popularCodeLanguagesAtom = atomWithStorage<Record<string, number>>(
  storageKeys.popularCodeLanguages,
  {
    javascript: 10,
    xml: 10,
    scss: 10,
    python: 10,
    sql: 10,
  },
);

export const lastCodeBlockSyntaxAtom = atomWithStorage<string | null>(
  storageKeys.lastCodeBlockSyntax,
  null,
);

export const emojiSkinToneModifierAtom = atomWithStorage<string | null>(
  storageKeys.emojiSkinToneModifier,
  null,
);

const uniqWithLimit = <T>(list: T[], maxLength: number) => {
  return Array.from(new Set(list)).slice(0, maxLength);
};

export const useRecentlyUsedEmojis = () => {
  return useAtomValue(recentlyUsedEmojisAtom);
};

export const useAddRecentlyUsedEmoji = () => {
  const HISTORY_MAX_LENGTH = 100;

  const setEmojis = useSetAtom(recentlyUsedEmojisAtom);

  return useCallback(
    (char: string) => {
      const emoji = getEmojiItemByChar(char);
      if (emoji) {
        setEmojis((value) =>
          uniqWithLimit([emoji.name, ...value], HISTORY_MAX_LENGTH),
        );
      }
    },
    [setEmojis],
  );
};

export const useRecentlyMentionedUserIds = () => {
  return useAtomValue(recentlyMentionedUserIdsAtom);
};

export const useAddRecentlyMentionedUserId = () => {
  const HISTORY_MAX_LENGTH = 25;

  const setRecentlyMentionedUserIds = useSetAtom(recentlyMentionedUserIdsAtom);

  return useCallback(
    (userId: string) => {
      setRecentlyMentionedUserIds((value) =>
        uniqWithLimit([userId, ...value], HISTORY_MAX_LENGTH),
      );
    },
    [setRecentlyMentionedUserIds],
  );
};

export const useRecentlyUsedTemplateIds = () => {
  return useAtomValue(recentlyUsedTemplateIdsAtom);
};

export const useAddRecentlyUsedTemplateId = () => {
  const HISTORY_MAX_LENGTH = 10;

  const setRecentlyMentionedUserIds = useSetAtom(recentlyUsedTemplateIdsAtom);

  return useCallback(
    (templateId: string) => {
      setRecentlyMentionedUserIds((value) =>
        uniqWithLimit([templateId, ...value], HISTORY_MAX_LENGTH),
      );
    },
    [setRecentlyMentionedUserIds],
  );
};

export const useRecentlyViewedPostIds = () => {
  return useAtomValue(recentlyViewedPostIdsAtom);
};

export const useAddRecentlyViewedPostIds = () => {
  const HISTORY_MAX_LENGTH = 25;
  const setRecentlyViewedPostIds = useSetAtom(recentlyViewedPostIdsAtom);

  return useCallback(
    (postIds: string[]) => {
      setRecentlyViewedPostIds((value) =>
        uniqWithLimit([...postIds, ...value], HISTORY_MAX_LENGTH),
      );
    },
    [setRecentlyViewedPostIds],
  );
};

export const usePopularCodeLanguages = () => {
  return useAtomValue(popularCodeLanguagesAtom);
};

export const useAddPopularCodeLanguage = () => {
  const setPopularCodeLanguages = useSetAtom(popularCodeLanguagesAtom);

  return useCallback(
    (language: string) => {
      if (language === PlainLanguage.key) {
        return;
      }

      setPopularCodeLanguages((value) => ({
        ...value,
        [language]: (value[language] ?? 0) + 1,
      }));
    },
    [setPopularCodeLanguages],
  );
};

export const purgeUIPersistedState = () => {
  for (const key of Object.values(storageKeys)) {
    localStorage.removeItem(key);
  }
};
